import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Star, BookOpen, Users, ArrowRight, Award, TrendingUp, Shield, Gift, Rocket, CheckCircle, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { icon: BookOpen, title: 'Get Free Books', desc: 'Claim advance copies of upcoming books before they launch. Read for free.' },
  { icon: Star, title: 'Review & Earn Credits', desc: 'Post honest reviews on Amazon. Earn 10-15 credits for every verified review.' },
  { icon: Rocket, title: 'Launch Your Own Book', desc: 'Create a launch campaign to distribute ARCs and get reviews on day one.' },
  { icon: TrendingUp, title: 'Boost Rankings', desc: 'More reviews = higher Amazon rankings = more sales. The cycle keeps going.' },
];

const BENEFITS = [
  { icon: Gift, title: '50 Free Credits on Signup', desc: 'Start immediately. UPost subscribers get 100 bonus credits.' },
  { icon: BookOpen, title: 'Free Advance Copies', desc: 'Claim ARC copies of upcoming books. Read before anyone else, for free.' },
  { icon: Star, title: 'Earn Per Review', desc: 'Earn 10-15 credits for every verified Amazon review. Build your balance fast.' },
  { icon: Award, title: 'Badges & Leaderboard', desc: 'Climb from Bronze to Platinum. Top reviewers get featured on the leaderboard.' },
  { icon: Shield, title: 'Verified Reviews', desc: 'All reviews verified via Amazon links — ensuring authenticity and trust.' },
  { icon: Rocket, title: 'Launch Campaigns', desc: 'Authors can create campaigns to distribute ARCs and coordinate reviews on launch day.' },
];

export default function ClubLanding() {
  const [launches, setLaunches] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [genres, setGenres] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genreOptions = ['Fiction', 'Non-Fiction', 'Romance', 'Thriller', 'Sci-Fi', 'Fantasy', 'Mystery', 'Self-Help', 'Business', 'Biography', 'Horror', 'Young Adult'];

  useEffect(() => {
    fetch(`${API}/api/club/launches`).then(r => r.json()).then(d => setLaunches((d.campaigns || []).slice(0, 3))).catch(() => {});
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/club/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email })
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('club_token', data.access_token);
        localStorage.setItem('club_name', name);
        localStorage.setItem('club_email', email);
      }
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SeoHead {...SEO.club} />
      <div className="min-h-screen bg-[#0a0a0f]">
        {/* Hero — ARC Hook */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-transparent to-transparent" />
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm mb-6">
                  <Rocket className="w-4 h-4" />
                  The Authors' Exchange
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="exchange-hero-title">
                  Get Free Books.<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">Boost Your Rankings.</span>
                </h1>
                <p className="text-lg text-gray-400 leading-relaxed mb-8 max-w-lg">
                  Claim free advance reader copies. Review books and earn credits. Launch your own book with a team of reviewers ready on day one.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="#signup">
                    <Button data-testid="exchange-join-btn" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-6 text-lg rounded-full">
                      Join Free — Get 50 Credits <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </a>
                  <Link to="/club/dashboard">
                    <Button data-testid="exchange-dashboard-btn" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 px-8 py-6 text-lg rounded-full">
                      Member Dashboard
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Free Credits on Signup', value: '50' },
                    { label: 'Credits Per Review', value: '10-15' },
                    { label: 'Cost to Join', value: 'Free' },
                    { label: 'Badge Tiers', value: '4' },
                  ].map((s, i) => (
                    <Card key={i} className="bg-gray-900/50 border-gray-800 text-center p-5">
                      <CardContent className="p-0">
                        <p className="text-3xl font-bold text-amber-400">{s.value}</p>
                        <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured ARC Launches */}
        {launches.length > 0 && (
          <section className="py-16 px-4 border-t border-gray-800">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="uppercase tracking-[0.2em] text-xs font-bold text-amber-400 mb-2">Available Now</p>
                  <h2 className="text-lg text-white">Free Advance Copies</h2>
                </div>
                <Link to="/club/dashboard" className="text-sm text-amber-400 hover:underline flex items-center gap-1">
                  Browse all <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {launches.map(c => {
                  const slotsLeft = (c.arc_slots || 20) - (c.slots_claimed || 0);
                  return (
                    <Card key={c.campaign_id} className="bg-gray-900/50 border-gray-800" data-testid={`launch-card-${c.campaign_id}`}>
                      <CardContent className="p-5">
                        <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{c.genre}</span>
                        <h3 className="text-lg font-semibold text-white mt-2 mb-1">{c.title}</h3>
                        <p className="text-xs text-gray-500 mb-3">by {c.author_name}</p>
                        <p className="text-sm text-gray-400 line-clamp-2 mb-4">{c.description}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(c.launch_date).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {slotsLeft > 0 ? `${slotsLeft} spots` : 'Full'}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* How It Works */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-amber-400 mb-4">How It Works</p>
              <h2 className="text-lg text-white">Four Steps to <span className="text-amber-400">More Reviews & Free Books</span></h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {STEPS.map((s, i) => (
                <Card key={i} className="bg-gray-900/50 border-gray-800 text-center p-6 relative" data-testid={`exchange-step-${i}`}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</div>
                  <CardContent className="pt-4 p-0">
                    <s.icon className="w-8 h-8 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-gray-400">{s.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Signup Form — the conversion point */}
        <section id="signup" className="py-20 px-4 bg-gray-900/20">
          <div className="container mx-auto max-w-xl">
            {submitted ? (
              <Card className="bg-gray-900/50 border-gray-800 text-center p-10">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-white mb-2">You're In!</h2>
                  <p className="text-gray-400 mb-6">Welcome to The Authors' Exchange. You've got 50 credits. Claim free books, review, and launch!</p>
                  <Link to="/club/dashboard">
                    <Button data-testid="exchange-go-dashboard" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                      Go to Dashboard
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-900/50 border-gray-800">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-white mb-2">Join The Authors' Exchange</h2>
                    <p className="text-gray-400 text-sm">Free advance copies + credit-based reviews. No password needed.</p>
                  </div>
                  <form onSubmit={handleSignup} className="space-y-4">
                    <Input data-testid="exchange-signup-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-gray-800 border-gray-700 text-white h-12" required />
                    <Input data-testid="exchange-signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" className="bg-gray-800 border-gray-700 text-white h-12" required />
                    <div>
                      <label className="text-sm text-gray-400 block mb-2">Genres you enjoy</label>
                      <div className="flex flex-wrap gap-2">
                        {genreOptions.map(g => (
                          <button key={g} type="button" onClick={() => setGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g])}
                            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${genres.includes(g) ? 'bg-amber-500 text-black font-medium' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    <Button data-testid="exchange-signup-submit" type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold py-6">
                      {loading ? 'Joining...' : 'Join Free — Get 50 Credits'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-amber-400 mb-4">Why Join</p>
              <h2 className="text-lg text-white">Everything You Get as a <span className="text-amber-400">Member</span></h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {BENEFITS.map((b, i) => (
                <Card key={i} className="bg-gray-900/50 border-gray-800 p-6" data-testid={`exchange-benefit-${i}`}>
                  <CardContent className="p-0 flex gap-4">
                    <b.icon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">{b.title}</h3>
                      <p className="text-sm text-gray-400">{b.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Leaderboard + CTA */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to <span className="text-amber-400">Get Started?</span></h2>
            <p className="text-gray-400 mb-8">Free books. Free credits. A community of authors helping each other succeed.</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="#signup">
                <Button data-testid="exchange-cta-join" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-10 py-6 text-lg rounded-full">
                  Join Free Now <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Link to="/club/leaderboard">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 px-8 py-6 text-lg rounded-full">
                  View Leaderboard
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
